import { useState } from 'react';
import { tours } from '../../../mocks/tours';

const categories = ['All'];

const difficultyColor: Record<string, string> = {
  Easy: 'bg-emerald-100 text-emerald-700',
  Medium: 'bg-amber-100 text-amber-700',
  Hard: 'bg-red-100 text-red-700',
};

type Tour = typeof tours[number];

function SkiBannerCard({ tour }: { tour: Tour & { includes: string[]; notIncludes: string[] } }) {
  const handleBookNow = () => {
    window.dispatchEvent(new CustomEvent('prefill-contact', { detail: { destino: tour.destination } }));
    const el = document.getElementById('contacto');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="col-span-1 md:col-span-2 lg:col-span-3 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 group cursor-pointer border-2 border-teal-100 bg-white">
      <div className="flex flex-col lg:flex-row">
        {/* Image */}
        <div className="relative w-full lg:w-1/2 h-72 lg:h-auto overflow-hidden">
          <img
            src={tour.image}
            alt={tour.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          {/* Badges */}
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-sky-500 text-white shadow flex items-center gap-1">
            <i className="ri-snowy-line"></i> Ski &amp; Snow
          </span>
          <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${difficultyColor[tour.difficulty]}`}>
            {tour.difficulty}
          </span>
          {/* Overlay info */}
          <div className="absolute bottom-4 left-4 right-4">
            <p className="text-white font-bold text-xl md:text-2xl leading-tight drop-shadow">{tour.name}</p>
            <div className="flex items-center gap-3 text-white/80 text-xs md:text-sm mt-1">
              <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{tour.destination}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>{tour.duration}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="w-full lg:w-1/2 p-6 md:p-8 flex flex-col justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Enjoy a full day of skiing and snow at <strong>El Colorado Ski Resort</strong>, perfect for beginners. Experience the Andes mountains just a short drive from Santiago.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              {/* Includes */}
              <div>
                <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i> Includes
                </p>
                <ul className="space-y-2">
                  {tour.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-checkbox-circle-fill text-teal-500 text-base"></i>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not Includes */}
              <div>
                <p className="text-xs font-bold tracking-widest text-red-400 uppercase mb-3 flex items-center gap-1">
                  <i className="ri-close-circle-line"></i> Not Included
                </p>
                <ul className="space-y-2">
                  {tour.notIncludes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
                      <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-close-circle-fill text-red-300 text-base"></i>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Rating + Price + CTA */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-5 border-t border-gray-100">
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <i className="ri-star-fill text-amber-400"></i>
                <span className="font-semibold text-gray-700">{tour.rating}</span>
                <span className="text-gray-400">({tour.reviews} reviews)</span>
              </div>
              <span className="text-xs text-gray-400">From</span>
              <p className="text-2xl md:text-3xl font-bold text-teal-600">
                ${tour.price}
                <span className="text-xs md:text-sm font-normal text-gray-400 ml-1">USD / person / day</span>
              </p>
            </div>
            <button
              onClick={handleBookNow}
              className="px-6 md:px-8 py-3 bg-sky-500 text-white text-sm font-semibold rounded-full hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap shadow-md flex items-center justify-center gap-2"
            >
              <i className="ri-snowy-line"></i>
              Book Ski Day
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PackageCard({ tour }: { tour: Tour & { includes: string[] } }) {
  const handleBookNow = () => {
    window.dispatchEvent(new CustomEvent('prefill-contact', { detail: { destino: tour.destination } }));
    const el = document.getElementById('contacto');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 group cursor-pointer border-2 border-teal-100 col-span-1">
      <div className="relative w-full h-56 overflow-hidden">
        <img
          src={tour.image}
          alt={tour.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold bg-teal-600 text-white shadow">
          ✦ Package
        </span>
        <span className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold ${difficultyColor[tour.difficulty]}`}>
          {tour.difficulty}
        </span>
        <div className="absolute bottom-3 left-4 right-4">
          <p className="text-white font-bold text-xl leading-tight drop-shadow">{tour.name}</p>
          <div className="flex items-center gap-2 text-white/80 text-xs mt-1">
            <i className="ri-map-pin-line"></i>
            <span>{tour.destination}</span>
            <span className="mx-1">·</span>
            <i className="ri-time-line"></i>
            <span>{tour.duration}</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">What's Included</p>
        <ul className="space-y-2 mb-5">
          {tour.includes.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                <i className="ri-checkbox-circle-fill text-teal-500 text-base"></i>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <i className="ri-star-fill text-amber-400"></i>
          <span className="font-semibold text-gray-700">{tour.rating}</span>
          <span className="text-gray-400">({tour.reviews} reviews)</span>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            {(tour as any).priceLabel ? (
              <p className="text-lg font-bold text-teal-600">{(tour as any).priceLabel}</p>
            ) : (
              <>
                <span className="text-xs text-gray-400">From</span>
                <p className="text-2xl font-bold text-teal-600">
                  ${tour.price}
                  <span className="text-sm font-normal text-gray-400 ml-1">USD</span>
                </p>
                <span className="text-xs text-gray-400">per person</span>
              </>
            )}
          </div>
          <button
            onClick={handleBookNow}
            className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap shadow"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}

function TourCard({ tour }: { tour: Tour }) {
  const handleBookNow = () => {
    window.dispatchEvent(new CustomEvent('prefill-contact', { detail: { destino: tour.destination } }));
    const el = document.getElementById('contacto');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 group cursor-pointer">
      <div className="relative w-full h-52 overflow-hidden">
        <img
          src={tour.image}
          alt={tour.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${difficultyColor[tour.difficulty]}`}>
          {tour.difficulty}
        </span>
        <span className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-teal-700">
          {tour.category}
        </span>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
          <i className="ri-map-pin-line"></i>
          <span>{tour.destination}</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-3 leading-snug">{tour.name}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <i className="ri-time-line text-teal-500"></i>
            <span>{tour.duration}</span>
          </div>
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400"></i>
            <span className="font-semibold text-gray-700">{tour.rating}</span>
            <span className="text-gray-400">({tour.reviews})</span>
          </div>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-400">From</span>
            <p className="text-2xl font-bold text-teal-600">
              ${tour.price}
              <span className="text-sm font-normal text-gray-400 ml-1">USD</span>
            </p>
            <span className="text-xs text-gray-400">per person</span>
          </div>
          <button
            onClick={handleBookNow}
            className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ToursActivities() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? tours
    : tours.filter((t) => t.category === activeCategory);

  return (
    <section id="tours" className="py-16 md:py-24 px-6 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <span className="inline-block text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">
              Curated Experiences
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Tours &amp; Activities
            </h2>
            <div className="w-20 h-1 bg-teal-600"></div>
          </div>
          <p className="text-base md:text-lg text-gray-500 max-w-md leading-relaxed md:text-right">
            Hand-picked adventures and cultural experiences across Chile's most iconic destinations.
          </p>
        </div>

        {/* Category Filter */}

        {/* Tours Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filtered.map((tour) => {
            const t = tour as any;
            if (t.isBanner) {
              return <SkiBannerCard key={tour.id} tour={t} />;
            }
            if ('includes' in tour && Array.isArray((tour as any).includes)) {
              return <PackageCard key={tour.id} tour={tour as Tour & { includes: string[] }} />;
            }
            return <TourCard key={tour.id} tour={tour} />;
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-gray-500 mb-4 text-sm">
            Looking for something specific? We create custom itineraries.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://www.civitatis.com/en/santiago-de-chile/search/?ag_aid=80811"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-md w-full sm:w-auto justify-center"
            >
              <i className="ri-compass-3-line"></i>
              Check More Tours
            </a>
            <button
              onClick={() => {
                const el = document.getElementById('contacto');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-teal-600 text-teal-600 font-semibold rounded-full hover:bg-teal-600 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto justify-center"
            >
              <i className="ri-customer-service-2-line"></i>
              Request a Custom Tour
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}